import React, { useEffect, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { Calendar, DateData } from 'react-native-calendars';
import { DateTime } from 'luxon';
import IconButton from '../../IconButton';
import Icon from '../../Icon';
import Text from '../../Text';
import Stack, { contentStackSpacing } from '../../Stack';
import { PromptActions, PromptContainer } from '../../Prompt';
import { PRScrollView } from '../../../shared/Scroll';
import { colors } from '../../../../utils/theme';
import { getSpacing } from '../../../../utils/spacing';
import { getIntl } from '../../../../utils/intl';
import TimeListField from './TimeListField';
import InlineListField from './InlineListField';
import { CALENDAR_DATE_FORMAT } from './utils';

const intl = getIntl('CalendarPickerPrompt', {
    dateTitle: 'Select Date',
    dateTimeTitle: 'Select Date & Time',
    time: 'Time',
    month: 'Month',
    year: 'Year',
    set: 'Set',
    cancel: 'Cancel',
});

const EARLIEST_YEAR = 1900;
// Shared with future-dated fields (invitation/insurance expiration) — offer years ahead too.
const YEARS_AHEAD = 10;
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    label: DateTime.utc(2024, i + 1, 1).toFormat('MMMM'),
    value: String(i + 1),
}));
const YEAR_OPTIONS = Array.from({ length: DateTime.local().year + YEARS_AHEAD - EARLIEST_YEAR + 1 }, (_, i) => {
    const year = String(DateTime.local().year + YEARS_AHEAD - i);
    return { label: year, value: year };
});

export type CalendarPickerPromptOwnProps = {
    type: 'date' | 'date-time';
    value: string | null; // ISO date or ISO datetime
    onChange: (value: string | null) => void;
    testID?: string;
};

export type CalendarPickerPromptProps = CalendarPickerPromptOwnProps;

const calendarTheme = {
    selectedDayBackgroundColor: colors.Primary,
    todayTextColor: colors.Primary,
    arrowColor: colors.Primary,
};

/**
 * Calendar-first picker for the shared date input — works on web AND native
 * (replaces the old web-only hidden <input type="date"> prompt).
 */
const CalendarPickerPrompt: React.FC<CalendarPickerPromptProps> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [date, setDate] = useState<string | null>(null);
    const [time, setTime] = useState<string | null>(null);
    const [visibleMonth, setVisibleMonth] = useState<DateTime>(() => DateTime.local().startOf('month'));

    useEffect(() => {
        if (!isOpen) return;
        const parsed = props.value ? DateTime.fromISO(props.value) : null;
        setDate(parsed?.isValid ? parsed.toFormat(CALENDAR_DATE_FORMAT) : null);
        setTime(parsed?.isValid && props.type === 'date-time' ? parsed.toFormat('HH:mm') : null);
        setVisibleMonth((parsed?.isValid ? parsed : DateTime.local()).startOf('month'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleSet = () => {
        if (!date) return;
        if (props.type === 'date') {
            props.onChange(date);
        } else {
            props.onChange(DateTime.fromFormat(`${date} ${time || '00:00'}`, `${CALENDAR_DATE_FORMAT} HH:mm`).toISO());
        }
        setIsOpen(false);
    };

    return (
        <>
            <IconButton
                icon="Calendar"
                onPress={() => {
                    Keyboard.dismiss();
                    setIsOpen(true);
                }}
                variant="minimal"
                testID={props.testID ? `${props.testID}-calendar-button` : undefined}
            />
            <PromptContainer
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                mobileMaxHeight={'85%'}
                renderPromptContents={() => (
                    <PRScrollView style={{ width: '100%' }}>
                        <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                            <Stack horizontal align="center" space={2.5} fullWidth>
                                <Icon name="Calendar" size="button-icon" />
                                <Text variant="h4">
                                    {intl(props.type === 'date-time' ? 'dateTimeTitle' : 'dateTitle')}
                                </Text>
                            </Stack>
                            {/* Always-visible month/year selectors — far-past dates (e.g. a 1945
                                DoB) are unreachable with one-month arrows alone. */}
                            <Stack horizontal fullWidth space={getSpacing(4)} align="flex-start">
                                <View style={{ flex: 1 }}>
                                    <InlineListField
                                        title={intl('month')}
                                        value={String(visibleMonth.month)}
                                        options={MONTH_OPTIONS}
                                        onChange={(month) =>
                                            setVisibleMonth(visibleMonth.set({ month: Number(month) }))
                                        }
                                        testID={props.testID ? `${props.testID}-month` : 'calendar-picker-prompt-month'}
                                    />
                                </View>
                                <View style={{ flex: 1 }}>
                                    <InlineListField
                                        title={intl('year')}
                                        value={String(visibleMonth.year)}
                                        options={YEAR_OPTIONS}
                                        onChange={(year) => setVisibleMonth(visibleMonth.set({ year: Number(year) }))}
                                        testID={props.testID ? `${props.testID}-year` : 'calendar-picker-prompt-year'}
                                    />
                                </View>
                            </Stack>
                            <View style={{ width: '100%' }}>
                                <Calendar
                                    // remount on jump: initialDate is only read on mount
                                    key={visibleMonth.toFormat('yyyy-MM')}
                                    initialDate={visibleMonth.toFormat(CALENDAR_DATE_FORMAT)}
                                    onDayPress={(day: DateData) => setDate(day.dateString)}
                                    onMonthChange={(month: DateData) =>
                                        setVisibleMonth(DateTime.fromISO(month.dateString).startOf('month'))
                                    }
                                    markedDates={
                                        date ? { [date]: { selected: true, selectedColor: colors.Primary } } : {}
                                    }
                                    theme={calendarTheme}
                                    testID={props.testID ? `${props.testID}-calendar` : 'calendar-picker-prompt'}
                                    style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}
                                />
                            </View>
                            {props.type === 'date-time' ? (
                                <TimeListField
                                    title={intl('time')}
                                    value={time}
                                    onChange={setTime}
                                    testID={props.testID ? `${props.testID}-time` : 'calendar-picker-prompt-time'}
                                />
                            ) : null}
                            <PromptActions
                                actions={[
                                    {
                                        title: intl('set'),
                                        onPress: handleSet,
                                        isDisabled: !date || (props.type === 'date-time' && !time),
                                        testID: props.testID ? `${props.testID}-set` : 'calendar-picker-prompt-set',
                                    },
                                    {
                                        title: intl('cancel'),
                                        variant: 'secondary',
                                        onPress: () => setIsOpen(false),
                                    },
                                ]}
                            />
                        </Stack>
                    </PRScrollView>
                )}
            />
        </>
    );
};

export default React.memo(CalendarPickerPrompt);
